import { SGR_DEFAULT_RATES } from 'lib/consts';
import {
  getConversionRatesFromRedux,
  getCurrencyConversionRate,
} from 'lib/hooks/useConvertionRates';
import { SubProductCreditLineConcentration } from 'lib/types/models';

export interface RiskPremiumRates {
  variableRate?: number;
  fixedRate?: number;
  variableDiscount?: number;
  fixedDiscount?: number;
}

type SubProduct = SubProductCreditLineConcentration;

export interface RiskPremiumCalculationInput {
  quotedAmount: number;
  days: number;
  quotedCurrency: 'CLP' | 'USD' | 'UF' | string;
  type: string;
  rates?: RiskPremiumRates;
  subProductsData?: SubProduct[];
  useConcentrationRates?: boolean;
  conversionRates?: {
    USD?: number;
    UF?: number;
  };
}

export interface RiskPremiumCalculationResult {
  amountInCLP: number;
  financialYear: number;
  guaranteeAmount: number;
  effectiveVariableRate: number;
  effectiveFixedRate: number;
  variableComponent: number;
  fixedComponent: number;
  amount: number;
  variableDiscount: number;
  fixedDiscount: number;
  originalVariableRate: number;
  originalFixedRate: number;
}

export function calculateRiskPremium(
  input: RiskPremiumCalculationInput,
): RiskPremiumCalculationResult | null {
  const {
    quotedAmount,
    days,
    quotedCurrency,
    type,
    rates = {},
    subProductsData = [],
    useConcentrationRates = false,
    conversionRates: customConversionRates,
  } = input;

  if (
    !(
      quotedAmount > 0 &&
      days >= 0 &&
      quotedCurrency &&
      quotedCurrency.trim() !== '' &&
      type &&
      type.trim() !== ''
    )
  ) {
    return null;
  }

  const defaultRates = getConversionRatesFromRedux();
  const conversionRates = {
    USD: customConversionRates?.USD ?? defaultRates.USD,
    UF: customConversionRates?.UF ?? defaultRates.UF,
  };
  const conversionRate = getCurrencyConversionRate(
    quotedCurrency,
    conversionRates,
  );
  const ufConversionRate = conversionRates.UF;

  let variableRate = SGR_DEFAULT_RATES.variableRate;
  let fixedRate = SGR_DEFAULT_RATES.fixedRate;
  let variableDiscount = SGR_DEFAULT_RATES.variableDiscount;
  let fixedDiscount = SGR_DEFAULT_RATES.fixedDiscount;

  if (
    useConcentrationRates &&
    Array.isArray(subProductsData) &&
    subProductsData.length > 0
  ) {
    const subProduct = subProductsData.find(
      (sp: SubProduct) => sp.subProductId === type,
    );
    if (subProduct) {
      variableRate = subProduct.operationCostVariable ?? variableRate;
      fixedRate = subProduct.operationCostFixed ?? fixedRate;
      variableDiscount = subProduct.discountRateVariable ?? variableDiscount;
      fixedDiscount = subProduct.discountRateFixed ?? fixedDiscount;
    }
  }

  variableRate = rates.variableRate ?? variableRate;
  fixedRate = rates.fixedRate ?? fixedRate;
  variableDiscount = rates.variableDiscount ?? variableDiscount;
  fixedDiscount = rates.fixedDiscount ?? fixedDiscount;

  const amountInCLP = quotedAmount * conversionRate;
  const financialYear = days / 360;
  const guaranteeAmount = amountInCLP * financialYear;

  const effectiveVariableRate = variableRate * (1 - variableDiscount / 100);
  const effectiveFixedRate = fixedRate * (1 - fixedDiscount / 100);

  const variableComponent = Math.floor(
    guaranteeAmount * (effectiveVariableRate / 100),
  );
  const fixedComponent = effectiveFixedRate * ufConversionRate;

  const amount = variableComponent + fixedComponent;

  return {
    amountInCLP,
    financialYear,
    guaranteeAmount,
    effectiveVariableRate,
    effectiveFixedRate,
    variableComponent,
    fixedComponent,
    amount,
    variableDiscount,
    fixedDiscount,
    originalVariableRate: variableRate,
    originalFixedRate: fixedRate,
  };
}
